import {
  AuthUtils,
  CloudErrors,
  CloudUtils,
  CostCenter,
  FlowjoCommons,
  LicenseUtils,
  Product,
} from '@portal/portal-core';
import { fileSave, supported } from 'browser-fs-access';
import type { FirstFileSaveOptions } from 'browser-fs-access';
import {
  ACTIVE_ADMIN,
  ACTIVE_INDIVIDUAL_TRIAL,
  ACTIVE_INSTITUTIONAL_TRIAL,
  ACTIVE_LICENSE,
  EXPIRED_TRIAL,
  EXPIRING_SOON,
  INACTIVE_LICENSE,
  NO_ACTION,
} from 'constants/product-tab';
import { LICENSE_STATUS, type LicenseStatus } from 'constants/super-admin';
import { differenceInDays } from 'date-fns';
import { format as dateFormat } from 'date-fns';
import { isEqual as dateIsEqual } from 'date-fns';
import Lockr from './local-store';
import { chunk, flow, get, isEmpty, orderBy, over, overEvery, sortBy } from 'lodash-es';
import flag from 'node-env-flag';
import queryString from 'query-string';
import { parseDate } from './date-utils';

export { parseDate };

export { convertArrayOfObjectsToCSV, downloadCSV } from './csv-utils';
export {
  validateProductVersionCreation,
  isProductVersionActive,
} from './product-version-validation';

Lockr.prefix = 'portal.';

// TODO: organize these into separate files!
const { Regional, Alerts, Time } = FlowjoCommons;

type DateInput = string | number | Date;

// Minimal domain shapes for the object structures these utils actually read.
interface ProductAccessNameOwner {
  name: string;
  owner: string;
}
interface AdminCostCenter {
  productAccess: ProductAccessNameOwner[];
}

// Generic comparator over arbitrary object properties whose values may be any
// mix of comparable primitives (strings, numbers, Dates).
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous sortable object properties
type SortableRecord = Record<string, any>;

export const joinNestedObjectValues = (
  obj?: Record<string, unknown> | null,
  delimiter = ' ',
): string | undefined => {
  if (!obj) return;
  return Object.values(obj)
    .map((val) => {
      // convert any objects recursively
      if (typeof val === 'object') {
        return joinNestedObjectValues(val as Record<string, unknown> | null, delimiter);
      }

      return val;
    })
    .join(delimiter);
};

export function injectIntoElement(
  element: unknown,
  tagId: string,
  htmlTag: string,
  type: string,
  src: string,
  code: string,
): void {
  const tag = document.createElement(htmlTag) as HTMLElement & {
    type: string;
    src: string;
    charset: string;
  };
  tag.id = tagId;
  tag.type = type;
  tag.innerText = code;
  tag.src = src;
  tag.charset = 'utf-8';
  document.getElementById('body')!.appendChild(tag);
}

export const changeLocation = function (linkString: string): string {
  // window.location's setter type is `string & Location`; runtime assigns the string.
  window.location = linkString as string & Location;
  return linkString;
};

export function getObjectMatchesFromValueMatch(
  keysIn: string[],
  keyOut: string | null,
  objectArray: Record<string, unknown>[],
  matchString: string,
): unknown[] {
  const returnArray: unknown[] = [];
  const searchStringLowerCased = matchString.toLowerCase();

  for (let i = 0; i < objectArray.length; i++) {
    for (let j = 0; j < keysIn.length; j++) {
      if (objectArray[i][keysIn[j]]) {
        const value = objectArray[i][keysIn[j]];
        let matchFound = false;
        if (value === searchStringLowerCased) {
          matchFound = true;
        }
        if (keyOut && matchFound) {
          returnArray.push(objectArray[i][keyOut]);
        } else if (matchFound) {
          returnArray.push(objectArray[i]);
        }
      }
    }
  }

  return Array.from(new Set(returnArray));
}

export function dedupe<T>(array: T[]): T[] {
  const hashTable: Record<string, boolean> = {};
  return array.filter((element) => {
    const key = JSON.stringify(element);
    const match = Boolean(hashTable[key]);

    return match ? false : (hashTable[key] = true);
  });
}

// TODO move to flowjo-commons along with writing a variety of tests
/**
 * Requires four @parameters
 * @param {array} keysIn - an array of string keys with string values should be searched if those
 * key(s) exist at the top level of the objects provided.
 * @param {string} keyOut - Optional. If set to null, the function will return an array of
 * unmutated objects where matches were found. If parameter is provided, the function will only
 * return an array of the values of the key for each object parsed. This key must be present in all
 * the objects in the objectArray for the function to work.
 * @param {array} objectArray - The array of objects that will be parsed. Must be a flat sibling
 * object structure.
 * Example: {{"id": 1, "name": "the title of obj 1"}, {"id": 2, "name": "the title of obj 2"}}
 * @param {string} searchString - The string to use for the matching.
 * @returns {array} - deduped array of objects which contain a match unless the keyOut has been
 * provided. If a valid keyOut has been provided, the array will contain a simple list of the
 * deduped keyOut values. For example, if the keyOut is a string of "id", the function will return
 * an array of ids that can be used as keys for for some sort of additional operation.
 * */
export function getObjectMatchesFromSearch(
  keysIn: string[],
  keyOut: string | null,
  objectArray: Record<string, string>[],
  searchString: string,
): unknown[] {
  const returnArray: unknown[] = [];

  for (let i = 0; i < objectArray.length; i++) {
    for (let j = 0; j < keysIn.length; j++) {
      if (objectArray[i][keysIn[j]]) {
        const value = objectArray[i][keysIn[j]];
        const sanitizedValue = value.replace(/[^\w\s]/gi, '');
        const sanitizedSearchString = searchString.replace(/[^\w\s]/gi, '');
        const matchFound = sanitizedValue.toLowerCase().match(sanitizedSearchString.toLowerCase());
        if (keyOut && matchFound) {
          returnArray.push(objectArray[i][keyOut]);
        } else if (matchFound) {
          returnArray.push(objectArray[i]);
        }
      }
    }
  }

  return Array.from(new Set(returnArray));
}

export function dynamicSortAscending(
  property: string,
): (a: SortableRecord, b: SortableRecord) => number {
  let sortOrder = 1;
  if (property[0] === '-') {
    sortOrder = -1;

    property = property.substr(1);
  }
  return function sortAscending(a: SortableRecord, b: SortableRecord): number {
    const result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
    return result * sortOrder;
  };
}

export function dynamicSortMultipleAscending(
  ...properties: string[]
): (obj1: SortableRecord, obj2: SortableRecord) => number {
  return function (obj1: SortableRecord, obj2: SortableRecord): number {
    let i = 0;
    let result = 0;
    const numberOfProperties = properties.length;
    while (result === 0 && i < numberOfProperties) {
      result = dynamicSortAscending(properties[i])(obj1, obj2);
      i += 1;
    }
    return result;
  };
}

export function isOdd(n: number): boolean {
  // Must be a number

  if (isNaN(n)) {
    return false;
  }

  // Number must not be a float
  if (n % 1 !== 0) {
    return false;
  }

  // Integer must not be equal to zero
  if (n === 0) {
    return false;
  }

  // Integer must be odd
  if (n % 2 !== 0) {
    return true;
  }

  return false;
}

export function shorten(text: string, maxLength: number): string {
  let mutateText = text;
  if (mutateText.length >= maxLength) {
    return (mutateText = `${mutateText.substr(0, maxLength - 3)}…`);
  }
  return text;
}

export function getEmailsFromUserIds(
  userIdsArray: Record<string, unknown>,
  users: Record<string, { emailAddress: string }>,
): string[] {
  const emailAddresses: string[] = [];

  for (const id in users) {
    if (userIdsArray[id]) {
      const emailAddress = users[id].emailAddress;
      emailAddresses.push(emailAddress);
    }
  }
  return emailAddresses;
}

export function isInArray(array: string[], string: string): boolean {
  return array.indexOf(string.toLowerCase()) > -1;
}

export function objectKeysToArray(
  origObj?: Record<string, unknown> | null,
): string[] | undefined {
  if (origObj) {
    return Object.keys(origObj);
  }
}

export const isProductOwner = ({
  products = [],
  userId,
}: { products?: Array<{ productAccess: { owner?: string } }>; userId?: string } = {}): boolean =>
  products.some((product) => product.productAccess.owner === userId);

export const isAdmin = (adminConfig?: {
  adminCostCenterList?: Record<string, unknown>;
} | null): boolean =>
  !!adminConfig &&
  !!adminConfig.adminCostCenterList &&
  !!Object.keys(adminConfig.adminCostCenterList).length;

export const isProductAdmin = ({
  costCenters = {},
  userId,
  productName = '',
  isInstitutional = false,
}: {
  costCenters?: Record<string, AdminCostCenter>;
  userId?: string;
  productName?: string;
  isInstitutional?: boolean;
} = {}): boolean => {
  if (isInstitutional) {
    return Object.values(costCenters).some(
      // For each cost center, check each product
      (costCenter) =>
        costCenter.productAccess.some(
          // For each product, check name -- returns immediately (from both loops) on match
          (product) =>
            product.name.toLowerCase() === productName.toLowerCase() && product.owner === userId,
        ),
    );
  }
  return false;
};

export const getProductAdminAccess = ({
  costCentersState,
  userId = '',
}: { costCentersState?: unknown; userId?: string } = {}): Record<string, boolean> => {
  const costCenterAdminChoices = Object.values(
    AuthUtils.getUserAdminCostCenters(userId, costCentersState),
  ) as Array<{ productAccess: Array<{ name: string }> }>;
  const productNames = Object.values(Product.NAMES_UI_TABS) as string[];
  // make an object where each productName is a key and the value is false
  const adminStats = productNames.reduce<Record<string, boolean>>(
    (acc, name) => ({ ...acc, [name]: false }),
    {},
  );

  costCenterAdminChoices.forEach((costCenter) => {
    costCenter.productAccess.forEach((product) => {
      adminStats[product.name] = true;
    });
  });

  return adminStats;
};

export const getUserInitials = ({
  firstname = '',
  lastname = '',
}: { firstname?: string | null; lastname?: string | null } = {}): string => {
  const getInitial = (name?: string | null): string => (name ? name.charAt(0).toUpperCase() : '');
  return getInitial(firstname) + getInitial(lastname);
};

export const getCostCenterInitials = (costCenterName = '', maxInitials = 1): string => {
  let result = '';
  if (costCenterName) {
    const splitName = costCenterName.split(/\W+/);
    for (let i = 0; i < maxInitials && i < splitName.length; i += 1) {
      result += splitName[i].charAt(0).toUpperCase();
    }
  }
  return result;
};

interface SortableUser {
  firstname?: string;
  lastname?: string;
  [key: string]: unknown;
}

// Builds an interatee function for lodash sorts to apply to user lists such that string properties
// produce expected sorts. See https://lodash.com/docs/4.17.5#orderBy
const buildUsersSort =
  (columnName?: string) =>
    (user: SortableUser): string | number | Date => {
    // `name` field combines first/last
      if (columnName === 'name') {
        return `${user.firstname} ${user.lastname}`;
      }
      // Parse time fields as real `Date` objects
      if (
        columnName === 'activationDate' ||
        columnName === 'deactivationDate' ||
        columnName === 'inviteSent' ||
        columnName === 'inviteExpires'
      ) {
        if (!user[columnName]) {
          return -1;
        }
        return new Date(user[columnName] as DateInput);
      }
      // Else, return as-is
      return user[columnName as string] as string | number | Date;
    };

// Takes an array of user objects and a sort config object of the shape
//  columnName: column to sort by
//  desc: boolean, sort in descending order?
export const getSortedUsers = (
  users: SortableUser[] = [],
  sortConfig: { columnName?: string; desc?: boolean } = {},
): SortableUser[] => {
  const iteratee = buildUsersSort(sortConfig.columnName);
  return orderBy(users, iteratee, (sortConfig.desc && 'desc') as false | 'desc');
};

// Takes an array of strings, and returns an object of the shape { someStringEntry: true } for
// fast lookups
export const formatListAsObject = (list: string[] = []): Record<string, boolean> =>
  list.reduce<Record<string, boolean>>(
    (total, current) => ({
      ...total,
      [current]: true,
    }),
    {},
  );

// Ensures that an email domain starts with "@", whether user has typed it or not
export const prefixEmailDomain = (domain = ''): string => `@${domain}`.replace(/^@@/, '@');

// Call with a count, word to pluralize, and optional plural form. If no plural form provided,
// defaults to adding "s".
export const pluralize = (count = 0, singularForm = '', pluralForm = ''): string => {
  if (count === 1) {
    return singularForm;
  }
  return pluralForm || `${singularForm}s`;
};

export const formatDate = (date?: DateInput | null, fallback = ''): string =>
  parseDate(date ?? null)
    ? dateFormat(parseDate(date ?? null) as Date, 'yyyy-MMM-dd')
    : fallback;

export const formatTime = (date?: DateInput | null, fallback = ''): string =>
  parseDate(date ?? null)
    ? dateFormat(parseDate(date ?? null) as Date, 'hh:mm:ss a')
    : fallback;

export const formatDateTime = (date?: DateInput | null): string =>
  date ? `${formatDate(date)} ${formatTime(date)}` : '';

// A product access feature as stored on a license (productAccess.features, keyed by id).
interface LicenseFeatureEntry {
  name?: string;
  enabled?: boolean;
  valueType?: string;
  value?: string | number | boolean | Date | null;
}

/**
 * Display lines for the features enabled on a license: the name for boolean features, otherwise
 * "name: value" when a value is set. Shared by the License Detail modal and the Manage License
 * change summary so both describe a license's features identically.
 */
export const formatEnabledFeatures = (features: unknown): string[] => {
  if (!features || typeof features !== 'object') return [];
  return Object.values(features as Record<string, LicenseFeatureEntry>)
    .filter((feature) => feature.enabled)
    .map((feature) => {
      if (feature.valueType === 'boolean') return feature.name ?? '';
      if (feature.value == null || feature.value === '') return feature.name ?? '';
      if (feature.valueType === 'date' && typeof feature.value !== 'boolean') {
        return `${feature.name}: ${formatDate(feature.value, String(feature.value))}`;
      }
      return `${feature.name}: ${feature.value}`;
    });
};

export const buildServicePeriod = (
  startDateFromSort?: DateInput | null,
  endDateFromSort?: DateInput | null,
): string => {
  if (!startDateFromSort && !endDateFromSort) {
    return '';
  }

  const startDate = parseDate(startDateFromSort ?? null)
    ? dateFormat(new Date(startDateFromSort as DateInput), 'MM/dd/yy')
    : '';

  const endDate = parseDate(endDateFromSort ?? null)
    ? dateFormat(new Date(endDateFromSort as DateInput), 'MM/dd/yy')
    : '';

  return `${startDate} - ${endDate}`;
};

export const getLicenseStr = (licenseState: string): string => {
  const licensesMap: Record<string, string> = {
    [ACTIVE_INDIVIDUAL_TRIAL]: 'Trial',
    [ACTIVE_INSTITUTIONAL_TRIAL]: 'Institutional Trial',
    [EXPIRED_TRIAL]: 'Expired Trial',
    [ACTIVE_LICENSE]: 'Active',
    [ACTIVE_ADMIN]: 'Active',
    [INACTIVE_LICENSE]: 'Expired',
  };

  return licensesMap[licenseState] || '';
};

// Takes an *array of arrays* of filter functions. Returns a single function that expects a single
// argument and returns true when:
//  - *All* (populated) child arrays meet the condition that
//  - *At least one* "grandchild" function per array passes
export const composeFilters =
  (filterGroups: Array<Array<(item: unknown) => boolean>> = []) =>
    (item: unknown): boolean =>
      filterGroups
      // Discard empty filter groups
        .filter((group) => !!group.length)
      // Require all arrays to pass
        .every(
        // Within groups, require only one to pass
          (group) => group.some((func) => func(item)),
        );

export const SEARCH_KEY = 'megaStringForSearch';

// Lodash's `omit` is both slow and bulky. Worth using a simple custom one:
export const omit = (
  obj: Record<string, unknown> = {},
  fields: string[] = [],
): Record<string, unknown> => {
  const clone = {
    ...obj,
  };
  fields.forEach((field) => delete clone[field]);
  return clone;
};

export const addSearchKey = (item: Record<string, unknown> = {}): Record<string, unknown> => ({
  ...item,
  [SEARCH_KEY]: joinNestedObjectValues(item),
});

export const stripSearchKey = (item: Record<string, unknown> = {}): Record<string, unknown> =>
  omit(item, [SEARCH_KEY]);

interface SuperAdminExportItem {
  admin: { firstname?: string; lastname?: string; primaryEmail?: string };
  costCenter: {
    admins?: unknown;
    id?: string;
    name?: string;
    countryCode?: string;
    whitelistDomains?: string[];
    whitelistIgnore?: unknown;
    type?: string;
  };
  productAccess: {
    features?: Record<string, { enabled?: boolean; name?: string }>;
    status?: unknown;
    activityState?: unknown;
    authorizedDevicesOverride?: unknown;
    endDate?: unknown;
    maxSharedComputers?: unknown;
    owner?: string;
    region?: unknown;
    maxActiveUsers?: unknown;
    startDate?: unknown;
    type?: string;
    name?: string;
    id?: string;
  };
  allocatedSeats?: unknown;
  activeSharedSeats?: unknown;
}

export const formatSuperAdminListForExport = (
  list: SuperAdminExportItem[] = {} as SuperAdminExportItem[],
): Record<string, unknown>[] =>
  list.map((item) => {
    const { admin, costCenter, productAccess } = item;
    // Format enabled features as a comma-separated list
    const enabledFeatures = productAccess.features
      ? Object.values(productAccess.features)
          .filter((feature) => feature.enabled)
          .map((feature) => feature.name)
          .join(', ')
      : '';
    return {
      status: productAccess.status,
      activityState: productAccess.activityState,
      admins: costCenter.admins,
      authorizedDevicesOverride: productAccess.authorizedDevicesOverride,
      billingDate: productAccess.endDate,
      costCenterID: costCenter.id,
      costCenterName: costCenter.name,
      country: costCenter.countryCode,
      endDate: productAccess.endDate,
      firstname: admin.firstname,
      lastname: admin.lastname,
      maxSharedComputers: productAccess.maxSharedComputers,
      ownerID: productAccess.owner,
      primaryEmail: admin.primaryEmail,
      region: productAccess.region,
      allocatedSeats: item.allocatedSeats,
      activeSharedSeats: item.activeSharedSeats,
      seatLimit: productAccess.maxActiveUsers,
      startDate: productAccess.startDate,
      type: productAccess.type,
      whitelistDomains: (costCenter.whitelistDomains || []).join('|'),
      whitelistIgnore: costCenter.whitelistIgnore,
      product: Product.PRODUCT_DISPLAY_NAMES[productAccess.name as string],
      productAccessId: productAccess.id,
      licenseType: costCenter.type,
      features: enabledFeatures,
    };
  });

const getNested = (theObject: unknown, path: string, separator = '.'): unknown => {
  try {
    const result = path
      .replace('[', separator)
      .replace(']', '')
      .split(separator)
      .reduce<unknown>((obj, property) => (obj as Record<string, unknown>)[property], theObject);
    return result;
  } catch {
    return undefined;
  }
};

interface SuperAdminLicense {
  primaryEmail?: string;
  name?: string;
  admin: { firstname?: string; lastname?: string };
  productAccess: { startDate?: string | null };
  [key: string]: unknown;
}

const buildSuperAdminSort =
  (columnName?: string) =>
    (license: SuperAdminLicense): string | number | Date => {
      if (columnName === 'siteAdminCostCenter') {
        return `${license.primaryEmail} ${license.name}`;
      }

      if (columnName === 'userNameContact') {
        return `${license.admin.firstname} ${license.admin.lastname}`;
      }

      // parse time fields as Date objects
      // handle service period separately since it is comprised of multiple dates
      // we'll just sort based on the start date of the service period
      if (columnName === 'servicePeriod') {
        if (!license.productAccess.startDate) {
          return -1;
        }
        return new Date(license.productAccess.startDate);
      }

      // for parsing columnNames that are nested properties. `i.e. productAccess.name`
      if (columnName?.includes('.')) return getNested(license, columnName) as string | number | Date;

      // Else, return as-is
      return license[columnName as string] as string | number | Date;
    };

// Takes an array of user objects and a sort config object of the shape
//  columnName: column to sort by
//  desc: boolean, sort in descending order?
export const sortSuperAdmin = (
  licenses: SuperAdminLicense[] = [],
  sortConfig: { columnName?: string; desc?: boolean } = {},
): SuperAdminLicense[] => {
  const iteratee = buildSuperAdminSort(sortConfig.columnName);
  return orderBy(licenses, iteratee, (sortConfig.desc && 'desc') as false | 'desc');
};

// Supports chaining `reselect` selectors in arbitrary order. The first argument should be a vanilla
// selector. Each additional arg should be a selector wrapped in a higher-order function that's
// called with the output of the previous transformation, to be consumed as input data.
export const chainSelectors = (
  entrySelector: unknown,
  ...transformationSelectors: Array<(...args: unknown[]) => unknown>
): unknown => flow(transformationSelectors)(entrySelector);

export const getSuperAdminExportFilename = (prefix = '', date: Date = new Date()): string =>
  `${prefix}-${dateFormat(date, 'yyyy-MM-dd_hh-mm-ss')}`;

interface LicenseDeepLink {
  productAccess: { name: string };
  costCenter: { id: string; owner: string };
}

export const buildLicenseDeepLink = (license: LicenseDeepLink): string =>
  `/admin?productName=${license.productAccess.name}&costCenterId=${license.costCenter.id}&adminID=${license.costCenter.owner}`;

// Wraps a Joi schema (usually from `cloud-core`) such that it returns a simple boolean
export const wrapJoiSchema =
  (schema: { validate: (val: unknown) => { error?: unknown } }) =>
    (val: unknown): boolean => {
      const res = schema.validate(val);
      return !res.error;
    };

type ValidationMessage = string | unknown[];
interface Validator {
  validator: (val: unknown) => boolean;
  getValidationMessage: (val: unknown) => ValidationMessage;
}

/**
 * @namespace composeValidators
 * @param {array} validators - an array of validator objects to compose into a single object
 * @returns {object} - an object containing both the `validator` function and the
 * `getValidationMessage` function for that set of validators. `getValidationMessage` returns each
 * relevant validator's error message on a separate line.
 */

export const composeValidators = (...validators: Validator[]): Validator => ({
  validator: overEvery(validators.map((v) => v.validator)),
  getValidationMessage: (val: unknown): ValidationMessage => {
    const results = over(validators.map((v) => v.validator))(val);
    const errorMessages = results
      .map((res, i) => (!res ? validators[i].getValidationMessage(val) : false))
      .filter(Boolean);
    const messagesAreStrings = errorMessages.every((msg) => typeof msg === 'string');
    return messagesAreStrings ? (errorMessages as string[]).join('\n') : (errorMessages as unknown[]);
  },
});

export const chinaFlag = (): boolean => {
  const china = Lockr.get('china');
  return !!china;
};

export const getCountryOptions = (): Array<{ label: string; value: string }> => {
  if (chinaFlag()) {
    return [
      {
        label: Regional.getCountryName('CN'),
        value: 'CN',
      },
    ];
  }
  return Object.keys(Regional.COUNTRY_NAMES_BY_CODE)
    .filter((code) => !(Regional.embargoedCountries as Record<string, unknown>)[code])
    .map((countryCode) => ({
      label: Regional.getCountryName(countryCode),
      value: countryCode,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const prettyPrintArray = (arr: unknown[] = []): string => {
  const bodyStr = arr.join(', ');
  return `[${bodyStr}]`;
};

// Detects changes within a superadmin license object
export const didLicensePropertyChange = (
  key: string,
  originalVal: unknown,
  currentVal: unknown,
): boolean => {
  switch (key) {
    case 'nextBillingDate':
      // This is "derived state" from `licenseEndDate`:
      return false;
    case 'licenseStartDate':
    case 'licenseEndDate':
      return !dateIsEqual(
        parseDate(originalVal as DateInput | null) as Date,
        parseDate(currentVal as DateInput | null) as Date,
      );
    case 'whitelistSuffixes':
      // Sorting handles case where suffix is removed and later re-added in different order:
      return (
        JSON.stringify(sortBy(originalVal as unknown[])) !==
        JSON.stringify(sortBy(currentVal as unknown[]))
      );
    default:
      // If key isn't special, standard equality check
      return currentVal !== originalVal;
  }
};

export const isSuperAdmin = (): boolean => flag(process.env.ENABLE__SUPER_ADMIN, false);

export const routeIsAdmin = (): boolean => get(window, 'location.pathname') === '/admin';

export const getQueryParamsforProduct = (testFlag = false): string => {
  const parameterToMatch = 'prod';
  if (!testFlag) {
    return Alerts.AlertUtils.getQueryVariable(parameterToMatch);
  }
  return '1';
};

export const deriveLicenseState = ({
  productLicenseInfo,
  userId,
  productName,
  costCenters,
  isInstitutional = false,
}: {
  productLicenseInfo?: {
    sortEndDate?: string;
    type?: string;
    active?: boolean;
    costCenterType?: string;
  } | null;
  userId?: string;
  productName?: string;
  costCenters?: Record<string, AdminCostCenter>;
  isInstitutional?: boolean;
} = {}): string | undefined => {
  const admin = isProductAdmin({ costCenters, userId, productName, isInstitutional });
  const expiringSoon =
    differenceInDays(Date.parse(productLicenseInfo?.sortEndDate as string), new Date()) <= 60;
  if (productLicenseInfo && !isEmpty(productLicenseInfo)) {
    const { type, active, costCenterType } = productLicenseInfo;
    if (
      type === CostCenter.PRODUCT_ACCESS_TYPES.trial &&
      active &&
      costCenterType === CostCenter.COST_CENTER_TYPES.institutional
    ) {
      return ACTIVE_INSTITUTIONAL_TRIAL;
    }
    if (
      type === CostCenter.PRODUCT_ACCESS_TYPES.trial &&
      active &&
      costCenterType === CostCenter.COST_CENTER_TYPES.individual
    ) {
      return ACTIVE_INDIVIDUAL_TRIAL;
    }
    if (type === CostCenter.PRODUCT_ACCESS_TYPES.trial && !active) {
      return EXPIRED_TRIAL;
    }
    if (active && expiringSoon) {
      return EXPIRING_SOON;
    }
    if (active) {
      return admin ? ACTIVE_ADMIN : ACTIVE_LICENSE;
    }
    if (!active) {
      return INACTIVE_LICENSE;
    }
  } else return NO_ACTION;
};

export const seatActive = (
  seatStartDate?: DateInput | null,
  seatEndDate?: DateInput | null,
  suspended?: boolean,
): boolean => {
  const now = new Date();
  const seatED = new Date(seatEndDate as DateInput);
  const seatSD = new Date(seatStartDate as DateInput);
  if (
    suspended ||
    (seatStartDate && seatSD.getTime() > now.getTime()) ||
    (seatEndDate && seatED.getTime() < now.getTime())
  ) {
    return false;
  }
  return true;
};

interface SuperAdminUserSeat {
  suspended?: boolean;
  startDate?: DateInput | null;
  endDate?: DateInput | null;
}

interface SuperAdminStatusProductAccess {
  activityState?: unknown;
  startDate?: DateInput | null;
  endDate?: DateInput | null;
  users?: Record<string, SuperAdminUserSeat | undefined>;
}

interface SuperAdminLicenseStatusOptions {
  /** Only meaningful for institutional cost centers. When true, userId's seat status is also checked. */
  userSeat?: boolean;
  userId?: string | null;
}

export const getSuperAdminLicenseStatus = (
  costCenterType: string | null | undefined,
  productAccess: SuperAdminStatusProductAccess,
  { userSeat = false, userId }: SuperAdminLicenseStatusOptions = {},
): LicenseStatus => {
  const isProductAccessActive = LicenseUtils.isProductAccessActive(productAccess);

  if (costCenterType !== CostCenter.COST_CENTER_TYPES.institutional) {
    return isProductAccessActive ? LICENSE_STATUS.ACTIVE : LICENSE_STATUS.INACTIVE;
  }

  if (!userSeat || !userId) {
    return isProductAccessActive ? LICENSE_STATUS.ACTIVE : LICENSE_STATUS.INACTIVE;
  }

  // userId is guaranteed non-null here: the guard above returns early if !userId.
  // A missing seat entry is not an error: the user may be an owner/admin without a seat
  // record, or the seat object may not yet have been written. In either case, fall back
  // to the product-access-level status (seat endDate is always null; expiry is only
  // tracked at the productAccess level for individual licenses).
  const seatData = productAccess.users?.[userId!];
  if (!seatData) {
    return isProductAccessActive ? LICENSE_STATUS.ACTIVE : LICENSE_STATUS.INACTIVE;
  }

  return AuthUtils.isUserSeatActive(seatData.suspended, seatData.startDate, seatData.endDate) &&
    isProductAccessActive
    ? LICENSE_STATUS.ACTIVE
    : LICENSE_STATUS.INACTIVE;
};

export const siteLicenseInfo = (productAccess: {
  startDate?: unknown;
  endDate?: unknown;
  activityState?: unknown;
  [key: string]: unknown;
}): {
  isSiteLicenseActive: boolean;
  startDate: unknown;
  endDate: unknown;
  activityState: unknown;
} => {
  const { startDate, endDate, activityState } = productAccess;
  const isSiteLicenseActive = LicenseUtils.isProductAccessActive(productAccess);
  return {
    isSiteLicenseActive,
    startDate,
    endDate,
    activityState,
  };
};

export const formatSharedComputerHWA = (hwa?: string | number | null): string =>
  `${hwa}`
    .toLowerCase()
    .replace(/[^0123456789abcdef]/g, '')
    .slice(0, 12);

export const formatHWADisplay = (hwa?: string | number | null): string =>
  chunk(formatSharedComputerHWA(hwa).split(''), 2)
    .map((pair) => pair.join(''))
    .join(':');

export const formatSharedComputerFingerprint = (fingerprint?: string | number | null): string =>
  `${fingerprint}`.replace(/[^0123456789abcdef]/g, '').slice(0, 16);

export const formatFingerprintDisplay = (fingerprint?: string | number | null): string =>
  formatSharedComputerFingerprint(fingerprint); // limit to 16 chars on entry

const genericSort = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous sortable values (Date | number | string | null)
  a: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous sortable values (Date | number | string | null)
  b: any,
): number => {
  if (!b) return -1;
  if (!a) return 1;
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
};

// Date comparison function for sorting
export const compareDates = (a: unknown, b: unknown): number => {
  const dataA = parseDate(a as DateInput | null);
  const dataB = parseDate(b as DateInput | null);
  return genericSort(dataA, dataB);
};

export const compareInts = (a: unknown, b: unknown): number => genericSort(a, b);

// String comparison function for sorting
export const compareStrings = (a: string, b: string): number =>
  new Intl.Collator('en-US', { numeric: true, caseFirst: 'false' }).compare(a, b);

// Generic comparison function for sorting HWA and Fingerprint
const compareHexValues = (
  a: string | null | undefined,
  b: string | null | undefined,
  formatter: (val: string) => string,
): number => {
  if (!a && !b) return 0; // Both are null/undefined
  if (!a) return -1; // Null/undefined comes first
  if (!b) return 1; // Null/undefined comes first

  const formattedA = formatter(a);
  const formattedB = formatter(b);

  const dataA = parseInt(formattedA, 16) || 0;
  const dataB = parseInt(formattedB, 16) || 0;

  return genericSort(dataA, dataB);
};

// HWA comparison function for sorting
export const compareHWAs = (a: string | null | undefined, b: string | null | undefined): number =>
  compareHexValues(a, b, formatSharedComputerHWA);

// Fingerprint comparison function for sorting
export const compareFingerprints = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => compareHexValues(a, b, formatSharedComputerFingerprint);

// Generic comparison function for sorting from identifier arrays
const compareIdentifiers = (
  a: Array<{ type: string; value?: string }>,
  b: Array<{ type: string; value?: string }>,
  type: string,
  compareFunction: (x?: string, y?: string) => number,
): number => {
  const dataA = a.find((identifier) => identifier.type === type)?.value;
  const dataB = b.find((identifier) => identifier.type === type)?.value;
  return compareFunction(dataA, dataB);
};

// Fingerprint comparison function for sorting from identifier arrays
export const compareSharedComputerFingerprintIdentifiers = (
  a: Array<{ type: string; value?: string }>,
  b: Array<{ type: string; value?: string }>,
): number =>
  compareIdentifiers(
    a,
    b,
    CostCenter.SHARED_COMPUTER_IDENTIFIER_TYPES.fingerprint,
    compareFingerprints,
  );

// HWA comparison function for sorting from identifier arrays
export const compareSharedComputerHWAIdentifiers = (
  a: Array<{ type: string; value?: string }>,
  b: Array<{ type: string; value?: string }>,
): number =>
  compareIdentifiers(a, b, CostCenter.SHARED_COMPUTER_IDENTIFIER_TYPES.hwa, compareHWAs);

// Name comparison function for sorting
export const compareNames = (
  a: { firstname?: string; lastname?: string; name?: string },
  b: { firstname?: string; lastname?: string; name?: string },
): number => {
  const getFullName = ({
    firstname,
    lastname,
    name,
  }: {
    firstname?: string;
    lastname?: string;
    name?: string;
  }): string => name || `${firstname} ${lastname}`;
  const dataA = getFullName(a);
  const dataB = getFullName(b);
  return compareStrings(dataA, dataB);
};

export const emptyObj = Object.freeze({});
export const emptyArray = Object.freeze([]);

// TODO check this regexp!
const VALID_URL_REGEX =
  /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)/; // eslint-disable-line no-useless-escape

/**
 * updates or adds query parameters to a url
 * <p>
 * @param {string} key - the name of the key of the query parameter
 * @param {string} value - the value associated with the key
 * @param {string} url - the url to modify
 * @returns {string} - the updated url with the desired query parameter
 */
// TODO verify why we are reassigning function params in this function
export const updateUrlQueryParameterValue = (
  key: string | null | undefined,
  value: unknown,
  url?: string,
): string => {
  /* no URL passed, use current url */
  if (!url) {
    url = window.location.href;
  }

  /* ensure valid url */
  if (!VALID_URL_REGEX.test(url)) throw Error('a valid url must be passed');

  /* invalid key passed, null/undefined and key REGEX */
  /* TODO invalid key is considered something that starts with a ? or =
   * though we probably need to check for more */
  // eslint-disable-next-line no-useless-escape
  const INVALID_KEY_REGEX = /^[\?\=]/;
  if (!key || INVALID_KEY_REGEX.test(key)) throw Error('a valid key must be passed');

  /* extract url query params */
  const extractedQueryString = queryString.extract(url);
  const queriesJSON = queryString.parse(extractedQueryString);

  /* create or update value for respective key */
  queriesJSON[key] = value as string;

  /* format queries to concat to URL */
  const queryParams = queryString.stringify(queriesJSON);

  /* extract url without query params */

  url = url.split('?')[0];

  /* concat queryParams to url */
  const newUrl = url.concat(`?${queryParams}`);
  return newUrl;
};

/**
 * signals a connection alert to the store
 * <p>
 * @param {Object} store - redux store
 * @returns {void}
 */
export function connectionAlert(store: { dispatch: (action: unknown) => unknown }): void {
  const errorType = CloudErrors.cloudErrorTypes.CONNECTION_PROBLEM;
  const alert = Alerts.Alert.AlertStamp({
    id: errorType,
    date: null,
    type: errorType,
  });
  const action = Alerts.AlertActions.setAlert(alert);
  store.dispatch(action);
}

/**
 * signals a to dismiss connection alert to the store
 * <p>
 * @param {Object} store - redux store
 * @returns {void}
 */
export function dismissConnectionAlert(store: { dispatch: (action: unknown) => unknown }): void {
  const errorType = CloudErrors.cloudErrorTypes.CONNECTION_PROBLEM;
  const action = Alerts.AlertActions.dismissAlert(errorType);
  store.dispatch(action);
}

// TODO func head
// TODO unit test
// changed to address FJCLOUD-2007 security vulnerability
export const navigate = (path: string): void => {
  location.assign(path);
};

export const navigateToPath = navigate;

// TODO func head
// TODO unit test
export const pausePolling = (): boolean | null => {
  const currentPathname = window.location.pathname;
  /* check if there is a pathname available */
  // Cast widens the literal narrowing TS infers in this `||` chain so the
  // final comparison isn't flagged as a no-overlap comparison. Runtime is unchanged.
  if (!currentPathname || currentPathname !== '/' || (currentPathname as string) !== '') {
    return true;
  }
  return null;
};

/**
 * gets value from a url object based on a property
 * <p>
 * @param {Object} url - url object to get property from
 * @param {string} property - the desired property
 * @returns {*} - desired property
 */
export const getValueForUrlProperty = (
  url: URL | null,
  property?: string,
): string | undefined => {
  if (url) {
    if (property === 'hash') {
      return url.hash;
    }
    if (property === 'host') {
      return url.host;
    }
    if (property === 'hostname') {
      return url.hostname;
    }
    if (property === 'href') {
      return url.href;
    }
    if (property === 'origin') {
      return url.origin;
    }
    if (property === 'pathname') {
      return url.pathname;
    }
    if (property === 'port') {
      return url.port;
    }
    if (property === 'protocol') {
      return url.protocol;
    }
    if (property === 'search') {
      return url.search;
    }
  }
  return undefined;
};

/**
 * sets a key value pair in local storage
 * <p>
 * @param {string} key - the key to set in local storage
 * @param {*} value - value to set for the desired key
 * @returns {boolean | null} - true if key value pair is set, null if no key value pair is set
 */
export const setValueInLocalStorage = (key: string | null, value: unknown): boolean | null => {
  if (key && value) {
    Lockr.set(key, value);
    return true;
  }
  return null;
};

/**
 * gets a value based on a key in local storage
 * <p>
 * @param {string} key - the key to set in local storage
 * @returns {boolean | null} - key value if key value pair exists, null if
 *               no key value passed, undefine if key does not exsit
 */
export const getValueInLocalStorage = (key: string | null): unknown => {
  if (key) {
    return Lockr.get(key);
  }
  return null;
};

/**
 * compares a value to the value associated with a key in local storage
 * <p>
 * @param {string} key - the key to set in local storage
 * @param {*} value - the value to compare to the value in local storage
 * @returns {boolean | null} - returns bool whether value matches with the value in local storage
 *               or null if the key doesn't exist
 */
export const compareValueToLocalStorage = (
  key: string | null,
  value?: unknown,
): boolean | null => {
  if (key) {
    const localStorageValue = Lockr.get(key);
    if (localStorageValue === value) return true;
    return false;
  }
  return null;
};

/**
 * sets current EPOCH time in cookies and local storage
 * <p>
 * @returns {void} - none
 */
export const setUserLastActiveTimestamp = (): void => {
  const timestamp = Time.getCurrentEpochTimeUtc();
  Lockr.set('lastUserActiveTimestamp', timestamp);
};

/**
 * remove local storage key value pair
 * <p>
 * @param {string} key - key to remove from localStorage entirely.
 * @returns {void} - none
 */
export const removeClientStorageValue = (key?: string | null): void => {
  if (key) Lockr.rm(key);
};

/**
 * set the value for the key 'productName' in localStorage
 * <p>
 * @param {string} productName - key to remove from localStorage entirely.
 * @returns {void} - none
 */
export const setProductName = (productName: string): void => {
  setValueInLocalStorage('productName', productName);
};

// NOTE frontend-utils
export const recordActiveUserTimestamps = (): void => {
  let hidden: string | undefined;
  const doc = document as Document & { msHidden?: unknown; webkitHidden?: unknown };
  if (typeof document.hidden !== 'undefined') {
    // Opera 12.10 and Firefox 18 and later support
    hidden = 'hidden';
  } else if (typeof doc.msHidden !== 'undefined') {
    hidden = 'msHidden';
  } else if (typeof doc.webkitHidden !== 'undefined') {
    hidden = 'webkitHidden';
    // eslint-disable-next-line no-undef
    (globalThis as typeof globalThis & { visibilityChange?: string }).visibilityChange =
      'webkitvisibilitychange';
  }

  (function cycleAppInFocusCheck() {
    if (document.hasFocus() || !(document as unknown as Record<string, unknown>)[hidden as string]) {
      setUserLastActiveTimestamp();
      setTimeout(cycleAppInFocusCheck, 10000);
    } else {
      setTimeout(cycleAppInFocusCheck, 10000);
    }
  })();
};

export const urlParameterValues = (): Record<string, string> =>
  window.location.search
    .slice(1)
    .split('&')
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, curr) => {
      const pair = curr.split('=');
      if (pair[0] === 'product') acc.productName = decodeURIComponent(pair[1]); // TODO - remove when 'product' is deprecated
      acc[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
      return acc;
    }, {});

export const getGridUnits =
  (units: number) =>
    ({ theme }: { theme: { grid: { units: (u: number) => number } } }): number =>
      theme.grid.units(units);

export const convertToStringMatchRelaxed = (
  stringUnrelaxed?: string | null,
): string | undefined => {
  if (stringUnrelaxed) return stringUnrelaxed.replace(/[\s\-;_/[\]{}()%.,:]/g, '').toLowerCase();
  return undefined;
};

export const isStringMatchRelaxed = (
  string1?: string | null,
  string2?: string | null,
): boolean => {
  if (
    string1 &&
    string2 &&
    convertToStringMatchRelaxed(string1) === convertToStringMatchRelaxed(string2)
  )
    return true;
  return false;
};

/* ----------------------------------------------------------------------------
 * XXX TO BE DEPRECATED
/* ---------------------------------------------------------------------------- */

// these functions are redundant
export const setClientStorageValue = (key?: string | null, value?: unknown): void => {
  if (key) {
    Lockr.set(key, value);
  }
};

// NOTE frontend-utils
export const getClientStorageValue = (key: string): unknown => Lockr.get(key);

export const testFunc = (): void => {
  // eslint-disable-next-line no-console
  console.log("HI, I'M THE FRONTEND UTILS PACKAGE :)");
};

export function costCenterInfoForSelectedAdminProductTab(
  adminCostCenterList: Record<string, { name: string; id: string; productAccess: Array<{ name: string }> }>,
  productName: string,
): { name: string; id: string; costCenter: { name: string; id: string; productAccess: Array<{ name: string }> } } | undefined {
  let specifiedCostCenter:
    | { name: string; id: string; costCenter: { name: string; id: string; productAccess: Array<{ name: string }> } }
    | undefined;
  // if there is a productAccess in the cost center with the same name as the productName
  // return that information
  if (adminCostCenterList && Object.keys(adminCostCenterList).length) {
    Object.values(adminCostCenterList).forEach((costCenter) => {
      const { productAccess } = costCenter;
      const hasProductName = productAccess.some((product) => product.name === productName);
      if (hasProductName) {
        specifiedCostCenter = {
          name: costCenter.name,
          id: costCenter.id,
          costCenter,
        };
      }
    });
  }
  return specifiedCostCenter;
}

export const costCenterDropdownInfo = (
  costCenters: Record<string, { id: string; name: string; productAccess: Array<{ name: string }> }>,
  productName: string,
): Array<{ id: string; name: string }> | undefined => {
  const costCenterList: Array<{ id: string; name: string }> = [];
  if (costCenters && Object.keys(costCenters).length > 0) {
    Object.values(costCenters).forEach((costCenter) => {
      const { id, name, productAccess } = costCenter;
      // filter the productAccess to only include items for the matching productName
      const matching = productAccess.filter((product) => product.name === productName);
      if (matching.length > 0) costCenterList.push({ id, name });
    });
    return costCenterList;
  }
};

export function defaultCostCenterObject(
  adminCostCenterList: Record<string, unknown>,
  productName: string,
): unknown {
  // cost center are set in URL query params. Return immediately:
  const params = CloudUtils.getCurrentQueryParams() as { costCenterId?: string };
  if (productName && params.costCenterId) {
    return adminCostCenterList[params.costCenterId] || undefined;
  }

  for (const adminCostCenterId in adminCostCenterList) {
    const adminCostCenterObj = adminCostCenterList[adminCostCenterId];
    const productLicense = CostCenter.findFirstProduct(adminCostCenterObj, productName);
    if (productLicense) return adminCostCenterList[adminCostCenterId];
  }
}

export function adminProductCheck(costCenterObj: unknown): {
  flowJoExists: boolean;
  seqGeqExists: boolean;
} {
  // UI requirement:
  // UI components need to determine whether Administrator has (for a single Cost Center)
  // productAccess(es) to just SeqGeq, just FlowJo, or both.
  const seqGeqExists = !!CostCenter.findFirstProduct(costCenterObj, Product.NAMES.seqGeq);
  const flowJoExists = !!CostCenter.findFirstProduct(costCenterObj, Product.NAMES.flowJo);

  return {
    flowJoExists,
    seqGeqExists,
  };
}

// Mirrors objectMap/objectMapMap's actual recursion: arrays and nested plain
// objects keep their shape, every other (leaf) value is replaced by mapFn's
// return type. Lets literal call sites (e.g. local-storage-keys) come out
// fully typed with no cast at the call site.
type DeepMapValue<T, R> = T extends (infer U)[]
  ? DeepMapValue<U, R>[]
  : T extends Record<string, unknown>
    ? { [K in keyof T]: DeepMapValue<T[K], R> }
    : R;

/**
 * returns a new object with the values at each key mapped using mapFn(value)
 * @param {Object} object - object to map
 * @param {Function} mapFn - function to map the values
 * @returns {Object} - new object with the values at each key mapped using mapFn(value)
 */
export const objectMap = <T extends Record<string, unknown>, R>(
  object: T,
  mapFn: (value: unknown) => R,
): DeepMapValue<T, R> =>
  Object.keys(object).reduce(
    (result, key) => ({
      ...result,

      [key]: objectMapMap(object[key], mapFn),
    }),
    {} as DeepMapValue<T, R>,
  );

const objectMapMap = <R>(value: unknown, mapFn: (value: unknown) => R): unknown => {
  switch (true) {
    case Array.isArray(value):
      return (value as unknown[]).map((item) => mapFn(item));
    case typeof value === 'object':
      return objectMap(value as Record<string, unknown>, mapFn);
    default:
      return mapFn(value);
  }
};

export const downloadTextFile = (filename: string, text: string): void => {
  const element = document.createElement('a');
  element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
  element.setAttribute('download', filename);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
};

export const downloadTextFileAsync = async (
  content: string,
  fileName = 'PortalDownload.txt',
  extension = '.txt',
): Promise<FileSystemFileHandle | null | undefined> => {
  if (supported) {
    const options: FirstFileSaveOptions = {
      fileName,
      extensions: [extension],
      excludeAcceptAllOption: true,
    };
    const blob = new Blob([content], { type: 'text/plain' });
    return fileSave(blob, options);
  }
  downloadTextFile(fileName, content);
};

const APPLICATION_NAME_IF_EXISTS_IN_LOCAL_STORAGE = getValueInLocalStorage('applicationLabel') as
  | string
  | undefined;
export const APPLICATION_NAME = APPLICATION_NAME_IF_EXISTS_IN_LOCAL_STORAGE || 'BD Access Portal';
